import React, { useCallback } from 'react';
import { Link, useHistory } from 'react-router-dom';
import classnames from 'classnames';
import pluralize from 'pluralize';
import { isEmpty, isNil } from 'ramda';
import dayjs from 'dayjs';
import { convertDuration } from '@/Utils/DurationFormatter';
import { formatEndedAtText, formatStartedAtText, isLiveNow, isUpcoming } from '@/Utils/DateTime';
import { formatRating } from '@/Utils/Rating';
import TileSize from '@/Components/Rail/Tiles/TileSize';
import VideoImage from '@/Components/VideoImage';
import Progress from '@/Components/Progress';
import Divider from '@/Components/Divider';
import { getWatchPath, getDetailPathByType } from '@/Utils/PageLocation';
import calculateProgress from '@/Utils/CalculateProgress';

import {
  GroupLayout,
  WatchProgress,
  Image,
  LinearSlot,
  Episode,
  Rating,
  StandardAspectRatio,
} from '@skytvnz/sky-app-store/lib/types/graph-ql';

import ContentType from '@/Utils/ContentTypes';
import { getGenres } from '@/Utils/Genres';
import { getVideoImageType } from '@/Utils/VideoImage';
import { getDateRange, isEpisodePromotedSlot } from '@/Utils/Slots';
import { utils } from '@/Store';
import { ReactComponent as InstantReplay } from '@skytvnz/sky-app-style/lib/assets/svg/icons/icon_instant_replay.color.svg';
import RailGroup from '@/types/enums/RailGroup';
import { getTimeRange } from '@/Utils/TimeFormat';
import styles from './styles.module.scss';

export interface Props {
  image?: Image;
  imageType?: string;
  id: string;
  episode?: Episode;
  title?: string;
  genres?: string[];
  year?: number;
  numberOfSeasons?: number;
  duration?: string;
  watchProgress?: WatchProgress;
  type: ContentType;
  slot?: LinearSlot;
  defaultEpisode?: Episode;
  rating?: Rating;
  aspectRatio?: StandardAspectRatio;
  railGroup?: RailGroup;
  layout?: GroupLayout;
  size?: TileSize;
  linkSearchQuery?: {};
  onClick?: () => void;
}

// BrandedProps are supposed to be only used by the component overrides unde 'src/brandedModules'
interface BrandedProps extends Props {
  showTextOnHover: boolean;
}
export const DIVIDER = ' • ';
const MediaTile: React.FC<BrandedProps> = props => {
  const {
    image,
    id,
    episode,
    showTextOnHover,
    title,
    genres,
    year = 0,
    numberOfSeasons = 0,
    duration,
    watchProgress,
    type,
    slot,
    defaultEpisode,
    rating,
    aspectRatio = StandardAspectRatio._16_9,
    railGroup = RailGroup.MediumRail,
    size = TileSize.Small,
    linkSearchQuery,
    onClick = () => undefined,
  } = props;
  const history = useHistory();
  const isContinueWatchingTile = railGroup === RailGroup.ContinueWatching;
  const isChannelTile = type === ContentType.LinearChannel;
  const isEpisodeTile = type === ContentType.Episode;
  const isLinearSlotTile = type === ContentType.LinearSlot;
  const isCollectionTile = type === ContentType.Collection || type === ContentType.BrowseCollection;
  const isCollectionLink = type === 'CollectionLink';
  const imageType = getVideoImageType(aspectRatio);
  const formattedDuration = convertDuration(duration);
  const contentOfferDate = getDateRange(episode)?.start;
  const formatcontentOfferDate = dayjs(contentOfferDate).format('ddd D MMM');
  const isInstantReplay = episode?.asset?.instantReplay ?? false;
  let isComingUp = false;
  let isLive = false;
  if (
    !isInstantReplay &&
    episode?.liveEvent &&
    episode.liveEvent.startTime &&
    episode.liveEvent.endTime
  ) {
    const { startTime, endTime } = episode.liveEvent;
    isComingUp = isUpcoming(startTime);
    isLive = isLiveNow(startTime, endTime);
  }

  let link = '';
  let watchPercent = 0;
  if (isLinearSlotTile || isEpisodePromotedSlot(episode)) {
    // Linear slot click is handled from onTileClick
  } else if (isCollectionLink) {
    const ids = id.split('?');
    link = `browse/${ids[0]}${ids.length > 1 ? `?genre=${ids[1]}` : ''}`;
  } else if (isContinueWatchingTile) {
    link = getWatchPath(history, id, defaultEpisode?.id, 'Continue Watching');
    if (type === 'Movie') {
      watchPercent = calculateProgress(duration, watchProgress);
    } else {
      watchPercent = calculateProgress(defaultEpisode?.duration, defaultEpisode?.watchProgress);
    }
  } else {
    link = getDetailPathByType(type, id, linkSearchQuery, episode?.id);
  }

  const HoverMeta = useCallback(() => {
    let mediaInfo: any = null;
    const showRating = isContinueWatchingTile && !!rating;
    if (type === 'Movie') {
      mediaInfo = (
        <>
          {year > 0 && <span data-testid="media-tile-year">{year}</span>}
          {formattedDuration && (
            <>
              {year > 0 && <Divider />}
              <span data-testid="media-tile-duration">{formattedDuration}</span>
            </>
          )}
        </>
      );
    } else if (isContinueWatchingTile) {
      const episodeNumber = utils.title.getSeasonEpisodeNumber(
        defaultEpisode?.season?.number,
        defaultEpisode?.number,
      );
      mediaInfo = episodeNumber ? (
        <span data-testid="media-tile-episode">{episodeNumber}</span>
      ) : null;
    } else if (numberOfSeasons > 0) {
      mediaInfo = (
        <span data-testid="media-tile-seasons">{pluralize('Season', numberOfSeasons, true)}</span>
      );
    }

    return (
      <div
        className={classnames(styles.posterMeta, {
          [styles.posterMetaWithProgressBar]: !!watchPercent,
        })}
      >
        <div className={styles.detailsTitle}>
          <h2 data-testid="media-tile-hover-title">{title}</h2>
        </div>
        <div className={styles.detailsAttributes}>
          {!isNil(genres) && !isEmpty(genres) && (
            <span className={styles.detailsGenres} data-testid="media-tile-genres">
              {getGenres(genres, 3)}
            </span>
          )}
          <div className={styles.detailsNotGenres}>
            {(year > 0 || numberOfSeasons > 0 || isContinueWatchingTile) && (
              <>
                {!isNil(genres) && !isEmpty(genres) && mediaInfo && <Divider />}
                {mediaInfo}
              </>
            )}
            {showRating && (
              <>
                <Divider />
                <span data-testid="hero-rating">{formatRating(rating)}</span>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }, [
    year,
    title,
    genres,
    numberOfSeasons,
    defaultEpisode,
    type,
    formattedDuration,
    isContinueWatchingTile,
    rating,
    watchPercent,
  ]);
  const getSubTitle = (episodeData: any) => {
    if (!episodeData) return null;

    const details: any = [];

    if (isLinearSlotTile) {
      details.push(formatStartedAtText(dayjs(episodeData?.start)));
      if (rating) {
        details.push(rating);
      }
    } else if (isEpisodePromotedSlot(episode)) {
      details.push(formatStartedAtText(dayjs(episode?.liveEvent?.startTime)));
    } else if (episodeData.liveEvent) {
      details.push(formatEndedAtText(dayjs(episode?.liveEvent?.endTime)));
    } else if (formatcontentOfferDate) {
      details.push(formatcontentOfferDate);
      if (formattedDuration) {
        details.push(formattedDuration);
      }
    }
    if (episodeData.rating) {
      details.push(formatRating(episodeData.rating));
    }

    return details.join(DIVIDER);
  };

  return (
    <div
      className={classnames([
        styles[`tile${size === TileSize.Large && 'L'}`],
        styles[`tile${size === TileSize.Small && 'S'}`],
        styles[`tile${size === TileSize.SmallSquare && 'Xs'}`],
      ])}
    >
      <Link
        to={link || '#'} // Provide a fallback to prevent navigation
        className={styles.link}
        data-testid="media-tile-button"
        onClick={event => {
          if (isLinearSlotTile) {
            event.preventDefault(); // Prevent navigation for linear slot tiles
            onClick?.();
          } else {
            onClick?.();
          }
        }}
      >
        <div className={styles.poster} data-testid="media-tile-poster">
          <VideoImage
            className={styles.tileImage}
            lazyload
            src={image?.uri}
            overlayImage={image?.overlayImages?.[0]}
            type={imageType}
            whiteBackground={isChannelTile}
            alt={`The poster of "${title}"`}
            data-testid="media-tile-img"
            icon={isEpisodeTile && isInstantReplay && InstantReplay}
            isUpcoming={isComingUp}
            isLiveNow={isLive}
          />
          {!!watchPercent && (
            <Progress
              className={styles.progressBar}
              percent={watchPercent}
              dataTestIdOuter={`continue-watching-progress-bar-outer-${id}`}
              dataTestIdInner={`continue-watching-progress-bar-inner-${id}`}
            />
          )}
          {!isChannelTile && !isCollectionTile && !isCollectionLink && showTextOnHover && (
            <HoverMeta />
          )}
        </div>
        <h3
          className={classnames(styles.meta, {
            [styles.extraInfo]: isChannelTile || isEpisodeTile || isLinearSlotTile,
          })}
          title={title}
          data-testid="media-tile-title"
        >
          <span>{slot?.programme?.title || title}</span>
          {isChannelTile && (
            <span className={styles.subTitle}>{getTimeRange(slot?.start, slot?.end)}</span>
          )}
          {(isLinearSlotTile || isEpisodeTile) && (
            <span className={styles.subTitle}>{getSubTitle(episode || slot)}</span>
          )}
        </h3>
      </Link>
    </div>
  );
};

export default MediaTile;
